import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import { DONATE_LINK_PROPS } from '../../config/donate';
import './LearnMore.css';

function LearnMore() {
  return (
    <div className="learn-more-page">
      <Helmet>
        <title>Learn About Mountain Pine Beetles | Lifecycle, Signs &amp; Prevention</title>
        <meta name="description" content="Learn about the mountain pine beetle (Dendroctonus ponderosae) lifecycle, how to identify signs of infestation like pitch tubes and needle discoloration, and proven prevention methods including forest thinning, preventive spraying, and pheromone treatments." />
        <meta name="keywords" content="mountain pine beetle lifecycle, Dendroctonus ponderosae, beetle infestation signs, pitch tubes, boring dust, needle discoloration, blue stain fungus, pine beetle prevention methods, forest thinning, verbenone, carbaryl, preventive spraying, sanitation harvesting, bark beetle education, forest protection" />
        <link rel="canonical" href="https://www.mountainpinebeetle.org/learn-more" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://www.mountainpinebeetle.org/learn-more" />
        <meta property="og:title" content="Learn About Mountain Pine Beetles | Lifecycle, Signs & Prevention" />
        <meta property="og:description" content="Understand the mountain pine beetle lifecycle, identify infestation signs, and learn proven prevention methods to protect your forests." />
        <meta property="og:site_name" content="Mountain Pine Beetle Foundation" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content="https://www.mountainpinebeetle.org/mpb_logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Learn About Mountain Pine Beetles | Lifecycle, Signs & Prevention" />
        <meta name="twitter:description" content="Understand the mountain pine beetle lifecycle, identify infestation signs, and learn proven prevention methods to protect your forests." />
        <meta name="twitter:image" content="https://www.mountainpinebeetle.org/mpb_logo.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Understanding the Mountain Pine Beetle",
            "description": "Learn about the mountain pine beetle lifecycle, how to identify signs of infestation, and proven prevention methods to protect forests.",
            "url": "https://www.mountainpinebeetle.org/learn-more",
            "publisher": {
              "@type": "Organization",
              "name": "Mountain Pine Beetle Foundation",
              "url": "https://www.mountainpinebeetle.org"
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://www.mountainpinebeetle.org/learn-more"
            }
          }
        `}</script>
      </Helmet>

      {/* Page Header */}
      <section className="learn-header py-5">
        <Container>
          <div className="text-center py-3">
            <h1 className="mb-3">Understanding the Mountain Pine Beetle</h1>
            <p className="mx-auto mb-0" style={{ maxWidth: '620px' }}>
              The mountain pine beetle (Dendroctonus ponderosae) is a species of bark beetle native to the forests of western North America. Understanding its biology and behavior is the first step toward effective prevention.
            </p>
          </div>
        </Container>
      </section>

      {/* Beetle Lifecycle */}
      <section className="py-5">
        <Container>
          <h2 className="section-header">Beetle Lifecycle</h2>
          <Row className="g-4">
            <Col md={6} lg={3}>
              <Card className="lifecycle-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <span className="lifecycle-number">1</span>
                    <Card.Title className="mb-0 fs-6">Attack (Summer)</Card.Title>
                  </div>
                  <Card.Text className="text-muted small">
                    Adult beetles emerge in mid-summer and fly to new host trees. They bore through the bark and release pheromones to attract more beetles for a mass attack.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="lifecycle-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <span className="lifecycle-number">2</span>
                    <Card.Title className="mb-0 fs-6">Egg Laying</Card.Title>
                  </div>
                  <Card.Text className="text-muted small">
                    Female beetles create vertical galleries under the bark where they lay eggs. A blue stain fungus is introduced, which blocks the tree's water and nutrient transport.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="lifecycle-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <span className="lifecycle-number">3</span>
                    <Card.Title className="mb-0 fs-6">Larvae (Winter)</Card.Title>
                  </div>
                  <Card.Text className="text-muted small">
                    Larvae feed on the inner bark throughout winter. Extended cold snaps below -30&deg;F can kill larvae, but warmer winters allow higher survival rates.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="lifecycle-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <span className="lifecycle-number">4</span>
                    <Card.Title className="mb-0 fs-6">Emergence (Summer)</Card.Title>
                  </div>
                  <Card.Text className="text-muted small">
                    New adult beetles emerge the following summer, completing the one-year lifecycle. Each female can produce up to 75 eggs, leading to exponential population growth.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Signs of Infestation */}
      <section className="py-5 bg-light">
        <Container>
          <h2 className="section-header">Signs of Infestation</h2>
          <Row className="g-4">
            <Col md={6}>
              <Card className="sign-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <Card.Title>Pitch Tubes</Card.Title>
                  <Card.Text className="text-muted">
                    Small masses of resin (pitch tubes) on the trunk are a tree's defense response to beetle boring. These popcorn-shaped globs of resin appear on the bark surface and indicate an active or recent attack.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="sign-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <Card.Title>Boring Dust</Card.Title>
                  <Card.Text className="text-muted">
                    Fine, reddish-brown sawdust (frass) accumulates in bark crevices and at the base of the tree. This is created as beetles bore through the bark to reach the inner phloem layer.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="sign-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <Card.Title>Needle Discoloration</Card.Title>
                  <Card.Text className="text-muted">
                    Needles turn from green to yellow to reddish-brown, typically starting at the top of the crown. By the time needles turn red, the tree is already dead. This color change usually appears the summer after attack.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="sign-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <Card.Title>Blue Stain Fungus</Card.Title>
                  <Card.Text className="text-muted">
                    Cutting into the sapwood reveals a blue-gray discoloration caused by the fungus beetles carry. This fungus clogs the tree's vascular system, preventing water and nutrient transport throughout the tree.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Prevention Methods */}
      <section className="py-5">
        <Container>
          <h2 className="section-header">Prevention &amp; Mitigation</h2>
          <Row className="g-4">
            <Col md={6}>
              <Card className="prevention-detail-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <Card.Title>Preventive Spraying</Card.Title>
                  <Card.Text className="text-muted">
                    Applying registered insecticides (such as carbaryl or bifenthrin) to the bark of high-value trees before beetle flight season. Treatment must be applied in spring before beetles emerge and is effective for one season.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="prevention-detail-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <Card.Title>Forest Thinning</Card.Title>
                  <Card.Text className="text-muted">
                    Reducing tree density through selective thinning improves the overall health and vigor of remaining trees. Well-spaced trees have more resources and are better able to produce resin to defend against beetle attack.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="prevention-detail-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <Card.Title>Pheromone Treatments</Card.Title>
                  <Card.Text className="text-muted">
                    Anti-aggregation pheromone packets (verbenone) can be stapled to trees to deter beetles. These synthetic pheromones signal to beetles that a tree is already fully colonized, redirecting them elsewhere.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="prevention-detail-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <Card.Title>Sanitation Harvesting</Card.Title>
                  <Card.Text className="text-muted">
                    Removing and destroying infested trees before beetles emerge prevents the spread to nearby healthy trees. Infested wood should be debarked, burned, or chipped to eliminate larvae living beneath the bark.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Quick Facts */}
      <section className="py-5 bg-light">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <div className="fact-box">
                <h3 className="mb-4 text-center">Quick Facts</h3>
                <div className="fact-item">
                  <span className="fact-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </span>
                  <p className="mb-0">Mountain pine beetles are about the size of a grain of rice (5mm) but can kill trees over 100 feet tall.</p>
                </div>
                <div className="fact-item">
                  <span className="fact-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </span>
                  <p className="mb-0">Since the late 1990s, bark beetles have killed billions of trees across millions of acres in western North America.</p>
                </div>
                <div className="fact-item">
                  <span className="fact-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </span>
                  <p className="mb-0">Healthy trees can often defend themselves by producing enough resin to "pitch out" attacking beetles.</p>
                </div>
                <div className="fact-item">
                  <span className="fact-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </span>
                  <p className="mb-0">Drought-stressed trees are significantly more vulnerable as they produce less defensive resin.</p>
                </div>
                <div className="fact-item">
                  <span className="fact-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </span>
                  <p className="mb-0">Primary host species include lodgepole, ponderosa, Scotch, and limber pines.</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-5">
        <Container>
          <Row className="align-items-center">
            <Col xs={12} lg={6} className="mb-4 mb-lg-0">
              <h2 className="section-header">Want to Help Protect Our Forests?</h2>
              <p className="text-muted mb-0">
                Join our community of volunteers, landowners, and conservation advocates working together to combat the mountain pine beetle epidemic. Every effort counts.
              </p>
            </Col>
            <Col xs={12} lg={6} className="text-lg-end">
              <a {...DONATE_LINK_PROPS} className="btn btn-donate btn-lg me-2 mb-2">
                Donate
              </a>
              <Button
                variant="primary"
                size="lg"
                as={Link}
                to="/contact"
                className="me-2 mb-2"
              >
                Contact Us
              </Button>
              <Button
                variant="outline-primary"
                size="lg"
                as={Link}
                to="/"
                className="mb-2"
              >
                Back to Home
              </Button>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
}

export default LearnMore;
